<template>
  <v-container fluid>
    <PageHeader :title="(isCreate ? 'Upload' : 'Edit') + ' ' + documentTypeName" icon="mdi-receipt-text-outline">
      <template #actions>
        <div v-show="!isCreate">
          <ActionDropdown :actions="actions" :performAction="performAction" />
        </div>
      </template>
    </PageHeader>
    <div class="ma-4">
      <v-card class="elevation-1 card mt-4 pa-4 card-form">
        <LoadingProgress :loading="loading" />
        <v-form ref="form">
          <v-card-text>
            <div class="entityForm">
              <EntityForm v-model:entity="document" :inputFields="documentFields">
                <v-data-table
                  v-if="isShared"
                  v-model="document.selectedParties"
                  :headers="headers"
                  :items="enhancedParties"
                  item-value="id"
                  show-select
                  return-object
                >
                </v-data-table>
              </EntityForm>
            </div>
          </v-card-text>
        </v-form>

        <v-card-actions>
          <v-spacer />
          <v-btn @click="save()" :disabled="loading || disableSave" size="large" color="primary" variant="flat"
            >Save</v-btn
          >
          <v-btn @click="cancel()" :disabled="loading" size="large" variant="flat">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </div>
  </v-container>
</template>

<script>
import Auth0Mixin from "../Auth0Mixin";
import PageHeader from "@/components/PageHeader.vue";
import DocumentServices from "@/services/admin/legalDocumentService";
import partyService from "@/services/partyService";
import partyStatus from "@/constants/partyStatus";
import downloadService from "@/services/downloadService";
import ActionDropdown from "@/components/ActionDropdown.vue";
import LegalDocumentTypes from "@/constants/legalDocumentTypeList";
//import LegalDocumentTypeList from "@/constants/documentTypeTemplateList";
import LoadingProgress from "@/components/LoadingProgress.vue";
import EntityForm from "@/components/EntityForm.vue";
// import ValidationRules from "@/validationRules";
import {
  LegalDocumentPrivacySetting,
  LegalDocumentPrivacySettingList,
} from "../../constants/legalDocumentPrivacySettings.js";
const DELETE_ACTION = "Delete";
const DOWNLOAD_ACTION = "Download";

const FIELDS = [
  {
    label: "Click to select the document",
    attribute: "file",
    type: "uploadFile",
    icon: "mdi-file-image",
    accept: "*.pdf,*.doc,*.png",
    hint: "Click to select the document",
  },
  { label: "Privacy Setting", type: "radio", attribute: "privacySettingId", items: LegalDocumentPrivacySettingList },
  { label: "Active", attribute: "isActive", type: "checkbox" },
];

export default {
  name: "LegalDocument",
  mixins: [Auth0Mixin],
  props: {
    legalDocumentTypeId: Number,
    passedId: String,
  },
  components: { LoadingProgress, EntityForm, ActionDropdown, PageHeader },
  data() {
    return {
      document: {
        id: 0,
        file: null,
        privacySettingId: 1,
        selectedParties: [],
        isActive: true,
      },
      originalActiveStatus: true,
      actions: [DELETE_ACTION],
      documentFields: FIELDS,
      parties: [],
      alreadySelectedParties: [],
      headers: [
        { title: "Name", key: "name" },
        { title: "Number", key: "number", sortable: false },
      ],
      isCreate: false,
      loading: false,
    };
  },
  methods: {
    async init() {
      this.loading = true;
      this.isCreate = this.passedId === "create";
      if (!this.isCreate) {
        this.actions.push(DOWNLOAD_ACTION);
      }

      this.documentFields.find(e => e.attribute === "file").disabled = !this.isCreate;

      if (this.isCreate) {
        this.document = {
          id: 0,
          privacySettingId: 1,
          file: null,
          legalDocumentTypeId: this.legalDocumentTypeId,
          isActive: true,
        };
      } else {
        this.document = await DocumentServices.get(Number(this.passedId));
        this.alreadySelectedParties = this.document.selectedParties;
        this.originalActiveStatus = this.document.isActive;
      }

      this.parties = (
        await partyService.search(true, "", 5, partyStatus.CONNECTED, {
          sortBy: [{ key: "name", order: "asc" }],
          page: 1,
          itemsPerPage: 100,
        })
      ).items;

      // Match selected parties to party objects for v-data-table selection
      if (!this.isCreate && this.document.selectedParties && this.document.selectedParties.length > 0) {
        const selectedIds = this.document.selectedParties.map(p => p.id);
        // Wait for computed properties to update, then set selected parties to matching objects
        this.$nextTick(() => {
          this.document.selectedParties = this.enhancedParties.filter(p => selectedIds.includes(p.id));
        });
      }

      this.loading = false;
    },
    async save() {
      // Handle Vuetify 3 v-file-input returning array
      const file =
        Array.isArray(this.document.file) && this.document.file.length > 0 ? this.document.file[0] : this.document.file;

      if (this.isCreate && !file) {
        return this.$root.$snackbar.error("Please select a file");
      }

      try {
        this.loading = true;
        let existingActiveDocument = (
          await DocumentServices.searchDocuments({
            legalDocumentTypeId: this.document.legalDocumentTypeId,
            isActive: true,
          })
        ).items.filter(item => item.id !== this.document.id);

        // we are replacing existing active if there is already active and create one or update exiting to active from inactive
        let replaceExistingActive =
          existingActiveDocument.length && (this.isCreate || (!this.originalActiveStatus && this.document.isActive));
        let saveDocument = true;
        if (replaceExistingActive) {
          var documentTypeName = LegalDocumentTypes.find(x => x.id === this.document.legalDocumentTypeId).name;
          saveDocument = await this.$root.$confirmDialog.open(
            "Replace existing document",
            `This will replace existing ${documentTypeName} active document. By clicking continue, the existing active document will be inactive, and this will become the new active ${documentTypeName} document`,
            "Continue"
          );
        }
        if (saveDocument) {
          var details = {
            detailsDto: {
              legalDocumentTypeId: this.document.legalDocumentTypeId,
              privacySettingId: this.document.privacySettingId,
              isActive: this.document.isActive,
              selectedParties: this.document.selectedParties,
            },
          };

          if (this.isCreate) await DocumentServices.uploadDocument(file, details);
          else await DocumentServices.updateDocument(this.document.id, details.detailsDto);

          this.$root.$snackbar.message("Successfully uploaded the document");
          this.loading = false;
          this.$router.push({ name: "LegalDocuments", params: { legalDocumentTypeId: this.legalDocumentTypeId } });
        } else {
          this.loading = false;
        }
      } catch (e) {
        console.error("Upload error:", e);
        if (e.response?.status === 500) {
          const errorMessage = e.response?.data?.error?.message || e.response?.data?.message || "Server error";
          this.$root.$snackbar.error(errorMessage);
        } else if (e.toString().includes("status code 501")) {
          this.$root.$snackbar.warn("The file was uploaded but extract data failed.");
        } else {
          const errorMessage =
            e.response?.data?.message || e.message || "Cannot upload this document, please contact support";
          this.$root.$snackbar.error(errorMessage);
        }
        this.loading = false;
        this.$router.push({ name: "LegalDocuments", params: { legalDocumentTypeId: this.legalDocumentTypeId } });
      }
    },
    async performAction(action) {
      if (action === DELETE_ACTION) {
        let confirm = await this.$root.$confirmDialog.open("Confirm Delete", "Are you sure?", "Delete");
        if (!confirm) return;

        await DocumentServices.deleteDocument(this.document.id);
        this.$root.$snackbar.message(`Document Deleted`);
        this.$router.push({ name: "LegalDocuments", params: { legalDocumentTypeId: this.legalDocumentTypeId } });
      }
      if (action === DOWNLOAD_ACTION) {
        downloadService.download(`/legaldocuments/${this.document.id}/download`);
      }
    },
    cancel() {
      this.$router.push({ name: "LegalDocuments", params: { legalDocumentTypeId: this.legalDocumentTypeId } });
    },
  },
  computed: {
    isShared: function() {
      return this.document.privacySettingId === LegalDocumentPrivacySetting.SHARED_WITH_PARTNERS;
    },
    enhancedParties: function() {
      return this.parties.map(x => ({
        isSelectable: !this.alreadySelectedParties.some(p => p.id === x.id),
        ...x,
      }));
    },
    documentTypeName() {
      return LegalDocumentTypes.find(x => x.id == this.legalDocumentTypeId)?.name;
    },
    disableSave: function() {
      return this.isShared && (this.document.selectedParties == null || this.document.selectedParties.length === 0);
    },
  },
};
</script>

<style scoped>
.entityForm {
  margin-top: 20px;
}
</style>
